import React from "react";
import PropTypes from "prop-types";
import { Pagination as ReactstrapPagination, PaginationItem, PaginationLink } from "reactstrap";

const Pagination = ({ totalPages, currentPage, onPageChange, siblingCount = 1 }) => {
  // If there's only one page, don't render pagination
  if (totalPages <= 1) return null;

  // Helper function to create a range of numbers
  const range = (start, end) => {
    const length = end - start + 1;
    return Array.from({ length }, (_, idx) => idx + start);
  };

  // Calculate the range of page numbers to display
  const createPaginationItems = () => {
    // Always show first and last page
    const firstPageIndex = 0;
    const lastPageIndex = totalPages - 1;

    // Calculate the range of pages to show around the current page
    let startPage = Math.max(0, currentPage - siblingCount);
    let endPage = Math.min(lastPageIndex, currentPage + siblingCount);

    // Adjust if we're near the start or end
    if (currentPage < siblingCount) {
      endPage = Math.min(lastPageIndex, 2 * siblingCount);
    }

    if (currentPage > lastPageIndex - siblingCount) {
      startPage = Math.max(0, lastPageIndex - 2 * siblingCount);
    }

    // Create the range of pages
    const pages = range(startPage, endPage);

    // Add ellipsis and first/last page if needed
    const items = [];

    // Always add first page
    items.push(
      <PaginationItem key="first" active={currentPage === firstPageIndex}>
        <PaginationLink onClick={() => onPageChange(firstPageIndex)}>
          {firstPageIndex + 1}
        </PaginationLink>
      </PaginationItem>
    );

    // Add ellipsis if needed
    if (startPage > firstPageIndex + 1) {
      items.push(
        <PaginationItem key="ellipsis-start" disabled>
          <PaginationLink>...</PaginationLink>
        </PaginationItem>
      );
    }

    // Add pages in the middle (excluding first and last if they're in the range)
    pages.forEach((page) => {
      if (page !== firstPageIndex && page !== lastPageIndex) {
        items.push(
          <PaginationItem key={page} active={currentPage === page}>
            <PaginationLink onClick={() => onPageChange(page)}>
              {page + 1}
            </PaginationLink>
          </PaginationItem>
        );
      }
    });

    // Add ellipsis if needed
    if (endPage < lastPageIndex - 1) {
      items.push(
        <PaginationItem key="ellipsis-end" disabled>
          <PaginationLink>...</PaginationLink>
        </PaginationItem>
      );
    }

    // Always add last page if it's not the same as first page
    if (lastPageIndex !== firstPageIndex) {
      items.push(
        <PaginationItem key="last" active={currentPage === lastPageIndex}>
          <PaginationLink onClick={() => onPageChange(lastPageIndex)}>
            {lastPageIndex + 1}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return items;
  };

  return (
    <ReactstrapPagination className="pagination pagination-rounded justify-content-end mb-2">
      <PaginationItem disabled={currentPage === 0}>
        <PaginationLink previous onClick={() => onPageChange(currentPage - 1)} />
      </PaginationItem>
      
      {createPaginationItems()}
      
      <PaginationItem disabled={currentPage === totalPages - 1}>
        <PaginationLink next onClick={() => onPageChange(currentPage + 1)} />
      </PaginationItem>
    </ReactstrapPagination>
  );
};

Pagination.propTypes = {
  totalPages: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  siblingCount: PropTypes.number,
};

export default Pagination;
